import { Head, Link, router, useForm } from "@inertiajs/react";
import { AlertTriangle, BookOpen, Check, Copy, Key, Loader2, Plus, RotateCcwKey, Trash } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { CodeExamplesCard } from "@/components/docs/code-examples-card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import AppLayout from "@/layouts/app-layout";
import { dashboardPath } from "@/routes";
import type { BreadcrumbItem } from "@/types";

interface Domain {
  id: number;
  hostname: string;
  verification_status?: "unverified" | "verified" | "pending" | "failed";
}

interface ApiKey {
  id: number;
  name: string;
  token_prefix: string;
  last_used_at: string | null;
  created_at: string;
  active: boolean;
}

interface Props {
  api_keys: ApiKey[];
  domains: Domain[];
  new_api_key_token: string | null;
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Dashboard", href: dashboardPath() },
  { title: "API Keys", href: "/api_keys" },
];

export default function ApiKeysIndex({ api_keys, domains, new_api_key_token }: Props) {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [showNewToken, setShowNewToken] = useState(!!new_api_key_token);
  const [copiedToken, setCopiedToken] = useState(false);
  const [rotatingId, setRotatingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const verifiedDomains = domains.filter((domain) => domain.verification_status === "verified");
  const activeApiKeys = api_keys.filter((key) => key.active);

  useEffect(() => {
    if (new_api_key_token) {
      setShowNewToken(true);
      setCopiedToken(false);
    }
  }, [new_api_key_token]);

  const {
    data: createForm,
    setData: setCreateForm,
    post: postCreateForm,
    processing: isCreating,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    reset: resetCreateForm,
  } = useForm({
    name: "OG Pilot API Key",
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    postCreateForm("/api_keys", {
      preserveScroll: true,
      onSuccess: () => {
        setIsCreateOpen(false);
        resetCreateForm();
      },
    });
  };

  const handleCopyToken = async () => {
    if (!new_api_key_token) return;
    await navigator.clipboard.writeText(new_api_key_token);
    setCopiedToken(true);
    toast.success("API key copied to clipboard");
    setTimeout(() => setCopiedToken(false), 2000);
  };

  const handleRotate = (id: number) => {
    if (!confirm("Rotate this API key? The old key will stop working immediately.")) return;
    setRotatingId(id);
    router.patch(
      `/api_keys/${id}`,
      { action_type: "rotate" },
      {
        preserveScroll: true,
        onFinish: () => setRotatingId(null),
      },
    );
  };

  const handleDelete = (id: number) => {
    if (!confirm("Revoke this API key? This action cannot be undone.")) return;
    setDeletingId(id);
    router.delete(`/api_keys/${id}`, {
      preserveScroll: true,
      onFinish: () => setDeletingId(null),
    });
  };

  const handleCopyField = async (value: string, fieldKey: string) => {
    await navigator.clipboard.writeText(value);
    setCopiedField(fieldKey);
    setTimeout(() => setCopiedField(null), 1500);
  };

  const formatDate = (date: string | null) => {
    if (!date) return "Never";
    return new Date(date).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="API Keys" />

      <div className="flex flex-1 flex-col gap-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">API Keys</h1>
            <p className="text-muted-foreground">Manage API keys for programmatic OG image generation</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline">
              <Link href="/docs" prefetch className="flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                Read Documentation
              </Link>
            </Button>
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Create API Key
                </Button>
              </DialogTrigger>
              <DialogContent>
                <form onSubmit={handleCreate}>
                  <DialogHeader>
                    <DialogTitle>Create API Key</DialogTitle>
                    <DialogDescription>
                      Create a new API key for generating OG images programmatically.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        placeholder="Production API Key"
                        value={createForm.name}
                        onChange={(e) => setCreateForm("name", e.target.value)}
                        required
                      />
                    </div>
                    <p className="text-muted-foreground text-xs">
                      API keys work across all verified domains on your account. You&apos;ll need at least one verified
                      domain to generate images.
                    </p>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isCreating || !createForm.name}>
                      {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Create Key
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* New API Key Alert */}
        {new_api_key_token && showNewToken && (
          <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
            <Key className="h-4 w-4 text-green-600" />
            <AlertTitle className="text-green-800 dark:text-green-200">API Key Created Successfully</AlertTitle>
            <AlertDescription className="space-y-3">
              <p className="text-green-700 dark:text-green-300">
                Copy your API key now. You won&apos;t be able to see it again!
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded bg-green-100 px-3 py-2 font-mono text-sm break-all dark:bg-green-900">
                  {new_api_key_token}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    void handleCopyToken();
                  }}
                  className="shrink-0"
                >
                  {copiedToken ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowNewToken(false)}
                className="text-green-700 dark:text-green-300"
              >
                I&apos;ve copied my key
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* No Domains Warning */}
        {domains.length === 0 && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>No domains registered</AlertTitle>
            <AlertDescription>
              Add and verify a domain before using the API. You can still create API keys now.
              <Button variant="link" className="ml-1 h-auto p-0" onClick={() => router.visit("/dashboard")}>
                Add a domain →
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {domains.length > 0 && verifiedDomains.length === 0 && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>No verified domains yet</AlertTitle>
            <AlertDescription>
              Verify at least one domain to start generating images with the API.
              <Button variant="link" className="ml-1 h-auto p-0" onClick={() => router.visit("/dashboard")}>
                Verify a domain →
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* API Keys Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Your API Keys
            </CardTitle>
            <CardDescription>Use these keys to sign JWTs for generating OG images.</CardDescription>
          </CardHeader>
          <CardContent>
            {api_keys.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Key</TableHead>
                    <TableHead>Scope</TableHead>
                    <TableHead>Last Used</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {api_keys.map((apiKey) => (
                    <TableRow key={apiKey.id}>
                      <TableCell className="font-medium">{apiKey.name}</TableCell>
                      <TableCell>
                        <Tooltip open={copiedField === `token-${apiKey.id}`}>
                          <TooltipTrigger asChild>
                            <code
                              className="bg-muted hover:bg-muted/80 cursor-pointer rounded px-2 py-1 text-sm transition-colors"
                              onClick={() => void handleCopyField(apiKey.token_prefix, `token-${apiKey.id}`)}
                            >
                              {apiKey.token_prefix}••••••••
                            </code>
                          </TooltipTrigger>
                          <TooltipContent>Copied!</TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <div className="text-muted-foreground text-sm">
                          {verifiedDomains.length > 0
                            ? `All verified domains (${verifiedDomains.length})`
                            : "No verified domains"}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">{formatDate(apiKey.last_used_at)}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{formatDate(apiKey.created_at)}</TableCell>
                      <TableCell>
                        {apiKey.active ? <Badge>Active</Badge> : <Badge variant="destructive">Revoked</Badge>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRotate(apiKey.id)}
                                disabled={!apiKey.active || rotatingId === apiKey.id}
                                title="Rotate key"
                              >
                                {rotatingId === apiKey.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <RotateCcwKey className="h-4 w-4" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Rotate key</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDelete(apiKey.id)}
                                disabled={!apiKey.active || deletingId === apiKey.id}
                                title="Revoke key"
                              >
                                {deletingId === apiKey.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash className="h-4 w-4" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Revoke key</TooltipContent>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <Key className="text-muted-foreground mb-4 h-12 w-12" />
                <h3 className="mb-2 text-lg font-semibold">No API keys yet</h3>
                <p className="text-muted-foreground mb-4 max-w-md text-center">
                  Create an API key to start generating OG images programmatically. Check out the{" "}
                  <a href="/docs" className="text-primary underline">
                    documentation
                  </a>{" "}
                  to learn more.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Start Guide */}
        {activeApiKeys.length > 0 && (
          <CodeExamplesCard
            apiKeys={activeApiKeys.map((key) => ({
              id: String(key.id),
              name: key.name,
              tokenPrefix: key.token_prefix,
            }))}
            domains={verifiedDomains.map((domain) => ({
              id: String(domain.id),
              hostname: domain.hostname,
            }))}
            showApiKeySelector={activeApiKeys.length > 0}
            showDomainSelector={verifiedDomains.length > 0}
            showCopyButton={true}
          />
        )}
      </div>
    </AppLayout>
  );
}
